import { useMemo } from 'react';
import moment from 'moment/moment';
import { getCalendarDates } from '@mgm/common/features/schedules';
import { dateFormatTZ, toFixedString } from '@mgm/common/utils';
import { TScheduleShift } from '../../../../types';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getScheduleList } from '../../../../store/schedules/selectors';
import { getMembers } from '../../../../store/team/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getCurrencyById } from '../../../../store/common/selectors';

export function useScheduleTotals(selectedDate: string) {
  const members = useAppSelector(getMembers);
  const scheduleList = useAppSelector(getScheduleList);
  const authenticateUser = useAppSelector(getAuthenticateUser);
  const getCurrency = useAppSelector(getCurrencyById);

  const totalData = useMemo(() => {
    const weekDates = getCalendarDates(moment(selectedDate));
    let totalHoursInSeconds = 0;
    let totalAmount = 0;
    const currencyAmounts: Record<string, number> = {};

    const shifts = weekDates.reduce((acc, date) => {
      const dateFormatted = dateFormatTZ(date, 'D/M/YYYY');
      const shiftsByDate = scheduleList[dateFormatted]?.schedules || [];

      if (shiftsByDate?.length) {
        acc[dateFormatted] = shiftsByDate.map((shift) => (shift.id ? shift : {} as TScheduleShift));
      }

      return acc;
    }, {} as { [key: string]: Array<TScheduleShift> });

    const targetDateShifts = shifts[dateFormatTZ(selectedDate, 'D/M/YYYY')] || [];

    targetDateShifts.forEach((shift) => {
      const currentMember = members.find((member) => member.email === shift.email);

      // Only count hours for hourly payment type
      const hours = shift.timeInSeconds / 3600;

      totalHoursInSeconds += shift.timeInSeconds;

      if (currentMember?.payment?.paymentType !== 'Salary') {
        const currencyAmount = Number(
          toFixedString(
            Number(currentMember?.payment?.paymentAmount || 0) * hours,
            2,
          ),
        );

        totalAmount += currencyAmount;

        if (currentMember?.payment?.paymentCurrencyId) {
          const currencyId = currentMember.payment.paymentCurrencyId;
          if (!currencyAmounts[currencyId]) {
            currencyAmounts[currencyId] = 0;
          }
          currencyAmounts[currencyId] += currencyAmount;
        }
      }
    });

    const totalHoursFormatted = `${Math.floor(totalHoursInSeconds / 3600)}:${moment.utc(totalHoursInSeconds * 1000).format('mm')}h`;

    const currencyIds = Object.keys(currencyAmounts);
    const isDisplayTooltip = currencyIds.length > 1;
    const currencies = isDisplayTooltip ? currencyIds.map((currencyId) => ({
      currencyId,
      currencyAmount: currencyAmounts[currencyId],
    })) : [];

    const baseCurrencyId = authenticateUser?.personnel?.paymentCurrencyId;
    const totalAmountFormatted = `${getCurrency(baseCurrencyId).mark}${toFixedString(totalAmount, 2)}`;

    return {
      totalHours: totalHoursFormatted,
      totalAmount: totalAmountFormatted,
      currencies,
      isDisplayTooltip,
    };
  }, [selectedDate, scheduleList, authenticateUser?.personnel?.paymentCurrencyId]);

  return {
    totalData,
  };
}
